import { useEscClose } from '../hooks/useEscClose';
import React, { useState, useRef, useEffect } from 'react';
import { 
  Users, Clock, CheckCircle, XCircle, Award, FileText, 
  ChevronDown, ChevronUp, Download, AlertTriangle, 
  RefreshCw, Eye, Pen, Edit3, Save, X, Unlock, BookOpen, Zap
} from 'lucide-react';
import axios from 'axios';

const cleanModuleTitle = (t) => (t || '').replace(/^Modul\s*\d+\s*:\s*/i, '');
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Signature Canvas Component
const SignatureCanvas = ({ onSave, onClear, label = "Unterschrift des Teilnehmers (mit Maus oder Touch zeichnen):" }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1A1A1A';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (hasSignature) {
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      onSave(dataUrl);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onClear();
  };

  return (
    <div className="space-y-3">
      <div className="text-sm text-sand-500 mb-2">
        {label}
      </div>
      <div className="border-2 border-dashed border-cream-300 rounded-xl overflow-hidden bg-cream-100">
        <canvas
          ref={canvasRef}
          width={400}
          height={150}
          className="cursor-crosshair touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>
      <button
        onClick={clearCanvas}
        className="text-sm text-sand-500 hover:text-terra flex items-center gap-1"
      >
        <RefreshCw className="w-4 h-4" />
        Unterschrift löschen
      </button>
    </div>
  );
};

// Participant Row Component
const ParticipantRow = ({ participant, authHeader, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [signatureData, setSignatureData] = useState('');
  const [instructorSignatureData, setInstructorSignatureData] = useState('');
  const [generating, setGenerating] = useState(false);
  
  // Learning time correction state
  const [editingModule, setEditingModule] = useState(null);
  const [newMinutes, setNewMinutes] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [correcting, setCorrecting] = useState(false);
  const [unlocking, setUnlocking] = useState(null);
  
  // Aufgaben-Snapshot state
  const [showTaskSnapshot, setShowTaskSnapshot] = useState(false);
  useEscClose(() => setShowTaskSnapshot(false));
  const [taskSnapshot, setTaskSnapshot] = useState(null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  
  const openTaskSnapshot = async () => {
    setShowTaskSnapshot(true);
    if (taskSnapshot) return;
    setLoadingSnapshot(true);
    try {
      const res = await axios.get(
        `${API}/admin/participant/${participant.user_id}/course/${participant.course_id}/task-answers`,
        authHeader
      );
      setTaskSnapshot(res.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Aufgaben-Antworten");
    }
    setLoadingSnapshot(false);
  };
  
  const downloadSnapshotPDF = async () => {
    try {
      const res = await axios.get(
        `${API}/admin/participant/${participant.user_id}/course/${participant.course_id}/task-answers/pdf`,
        { ...authHeader, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Aufgaben_Audit_${participant.user_name.replace(/\s/g,'_')}_${participant.course_id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch {
      toast.error("Fehler beim PDF-Download");
    }
  };

  const loadDetails = async () => {
    if (details) {
      setExpanded(!expanded);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/participant/${participant.user_id}/details/${participant.course_id}`,
        authHeader
      );
      setDetails(response.data);
      setExpanded(true);
    } catch (error) {
      toast.error("Fehler beim Laden der Details");
    }
    setLoading(false);
  };

  // 1-Klick: Lernzeit auf Soll-Wert (UE × 45 min) setzen
  const [settingSoll, setSettingSoll] = useState(null); // module_id being processed
  const [bulkSettingSoll, setBulkSettingSoll] = useState(false);

  const setCourseLearningTimeToSoll = async (onlyZero) => {
    const label = onlyZero
      ? 'Alle Module MIT 0 Min auf Soll setzen (Module mit vorhandener Lernzeit bleiben unangetastet)?'
      : 'ALLE Module unter Soll auf Soll setzen (auch Module mit teilweiser Lernzeit werden hochgesetzt)?';
    if (!window.confirm(
      `${label}\n\n` +
      `Teilnehmer: ${participant.user_name}\n` +
      `Kurs: ${participant.course_title || participant.course_id}\n\n` +
      `Wichtig: Bestehende Werte, die BEREITS ≥ Soll sind, werden NICHT verändert. ` +
      `Diese Aktion ist für Audit-Zwecke voll protokolliert.`
    )) return;

    setBulkSettingSoll(true);
    try {
      const params = new URLSearchParams({
        user_id: participant.user_id,
        course_id: participant.course_id,
        only_zero: onlyZero ? 'true' : 'false',
        reason: onlyZero
          ? 'Bulk: Alle 0-Min-Module auf Soll'
          : 'Bulk: Alle Module unter Soll auf Soll',
      });
      const response = await axios.post(
        `${API}/admin/set-course-learning-time-to-soll?${params}`,
        {},
        authHeader
      );
      const d = response.data || {};
      toast.success(
        `${d.updated_count || 0} Modul(e) aktualisiert · +${d.total_minutes_added || 0} Min (${d.total_ue_added || 0} UE)` +
        (d.skipped_already_at_soll_count ? ` · ${d.skipped_already_at_soll_count} bereits auf Soll` : ''),
        { duration: 6000 }
      );
      // Reload details
      const detailsRes = await axios.get(
        `${API}/admin/participant/${participant.user_id}/details/${participant.course_id}`,
        authHeader
      );
      setDetails(detailsRes.data);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Bulk-Setzen");
    }
    setBulkSettingSoll(false);
  };

  const setLearningTimeToSoll = async (moduleId, moduleTitle, ueCount) => {
    setSettingSoll(moduleId);
    try {
      const params = new URLSearchParams({
        user_id: participant.user_id,
        module_id: moduleId,
        reason: `1-Klick: Lernzeit auf Soll (${ueCount} UE × 45 min)`,
      });
      const response = await axios.post(
        `${API}/admin/set-learning-time-to-soll?${params}`,
        {},
        authHeader
      );
      const data = response.data || {};
      if (data.already_at_or_above_soll) {
        toast.info(data.message || 'Bereits auf Soll');
      } else {
        toast.success(data.message || 'Lernzeit auf Soll gesetzt');
      }
      // Reload details
      const detailsRes = await axios.get(
        `${API}/admin/participant/${participant.user_id}/details/${participant.course_id}`,
        authHeader
      );
      setDetails(detailsRes.data);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Setzen auf Soll");
    }
    setSettingSoll(null);
  };

  // Correct learning time for a module
  const correctLearningTime = async (moduleId, moduleTitle) => {
    if (!newMinutes || isNaN(parseInt(newMinutes))) {
      toast.error("Bitte gültige Minuten eingeben");
      return;
    }
    
    setCorrecting(true);
    try {
      const response = await axios.post(
        `${API}/admin/correct-learning-time`,
        {
          user_id: participant.user_id,
          module_id: moduleId,
          minutes: parseInt(newMinutes),
          reason: correctionReason || `Manuelle Korrektur für ${moduleTitle}`
        },
        authHeader
      );
      
      toast.success(response.data.message);
      setEditingModule(null);
      setNewMinutes('');
      setCorrectionReason('');
      
      // Reload details
      const detailsRes = await axios.get(
        `${API}/admin/participant/${participant.user_id}/details/${participant.course_id}`,
        authHeader
      );
      setDetails(detailsRes.data);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Korrigieren");
    }
    setCorrecting(false);
  };

  // Manual unlock module
  const unlockModule = async (moduleId, moduleTitle) => {
    if (!window.confirm(`Modul "${moduleTitle}" für ${participant.user_name} freischalten? Dies setzt Lernzeit, Quiz und Live-Session auf bestanden.`)) return;
    
    setUnlocking(moduleId);
    try {
      const params = new URLSearchParams({
        user_id: participant.user_id,
        module_id: moduleId,
        notes: 'Manuell freigeschaltet aus Teilnehmer-Fortschritt',
        session_date: new Date().toISOString().split('T')[0],
        session_duration_minutes: '0'
      });
      await axios.post(
        `${API}/admin/manual-unlock-module?${params.toString()}`,
        {},
        authHeader
      );
      toast.success(`Modul "${moduleTitle}" freigeschaltet!`);
      // Reload details
      const detailsRes = await axios.get(
        `${API}/admin/participant/${participant.user_id}/details/${participant.course_id}`,
        authHeader
      );
      setDetails(detailsRes.data);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Freischalten");
    }
    setUnlocking(null);
  };

  const generateCertificate = async () => {
    if (!signatureData) {
      toast.error("Bitte erst die Teilnehmer-Unterschrift einholen");
      return;
    }
    if (!instructorSignatureData) {
      toast.error("Bitte erst die Dozenten-Unterschrift einholen");
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/admin/certificate/generate`,
        {
          user_id: participant.user_id,
          course_id: participant.course_id,
          signature_data: signatureData,
          instructor_signature_data: instructorSignatureData
        },
        authHeader
      );
      
      // Download PDF
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${response.data.pdf_base64}`;
      link.download = response.data.filename;
      link.click();
      
      toast.success("Teilnahmebescheinigung erstellt und heruntergeladen!");
      setShowSignature(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    }
    setGenerating(false);
  };

  const progressPercent = (participant.completed_modules / participant.total_modules) * 100;
  
  return (
    <div className="bg-cream-200 rounded-xl border border-cream-300 overflow-hidden">
      {/* Main Row */}
      <div 
        className="p-4 cursor-pointer hover:bg-cream-200 transition-colors"
        onClick={loadDetails}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-terra to-terra-dark flex items-center justify-center text-sand-800 font-bold text-lg">
              {participant.user_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="font-semibold text-sand-800">{participant.user_name}</h3>
              <p className="text-sm text-sand-500">@{participant.username}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            {/* Progress */}
            <div className="text-center">
              <div className="text-lg font-bold text-terra">
                {participant.completed_modules}/{participant.total_modules}
              </div>
              <div className="text-xs text-sand-500">Module</div>
            </div>
            
            {/* UE */}
            <div className="text-center">
              <div className="text-lg font-bold text-terra">
                {participant.completed_ue}/{participant.total_ue}
              </div>
              <div className="text-xs text-sand-500">UE</div>
            </div>
            
            {/* Time */}
            <div className="text-center">
              <div className="text-lg font-bold text-terra flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {Math.floor(participant.total_time_minutes / 60)}h {participant.total_time_minutes % 60}m
              </div>
              <div className="text-xs text-sand-500">Lernzeit</div>
            </div>
            
            {/* Status */}
            <div className="flex items-center gap-2">
              {participant.ready_for_exam ? (
                <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Prüfungsbereit
                </span>
              ) : (
                <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  In Bearbeitung
                </span>
              )}
              
              {participant.certificate_issued && (
                <span className="px-3 py-1 bg-terra/20 text-terra rounded-full text-sm flex items-center gap-1">
                  <Award className="w-4 h-4" />
                  Bescheinigt
                </span>
              )}
              
              {/* Aufgaben-Snapshot Button */}
              <button
                onClick={(e) => { e.stopPropagation(); openTaskSnapshot(); }}
                className="px-3 py-1 bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 rounded-full text-sm flex items-center gap-1 transition-colors border border-blue-500/30"
                title="Alle eingereichten Antworten ansehen"
                data-testid={`view-task-answers-${participant.user_id}`}
              >
                <BookOpen className="w-3.5 h-3.5" />
                Aufgaben einsehen
              </button>
            </div>
            
            {loading ? (
              <RefreshCw className="w-5 h-5 text-sand-500 animate-spin" />
            ) : (
              expanded ? <ChevronUp className="w-5 h-5 text-sand-500" /> : <ChevronDown className="w-5 h-5 text-sand-500" />
            )}
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4 h-2 bg-cream-300 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-terra to-terra-dark transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
      
      {/* Expanded Details */}
      {expanded && details && (
        <div className="border-t border-cream-300 p-4 bg-cream-50">
          {/* Status-Schnellcheck Block - zeigt wo der Teilnehmer feststeckt */}
          {details.first_blocker ? (
            <div className="mb-6 p-4 rounded-xl bg-amber-500/5 border border-amber-500/30" data-testid={`status-check-${participant.user_id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <div className="flex-1">
                  <p className="text-amber-400 font-semibold mb-1">
                    Steckt fest an Modul {details.first_blocker.module_order}: {cleanModuleTitle(details.first_blocker.module_title)}
                  </p>
                  <p className="text-sm text-sand-500 mb-2">Was noch fehlt:</p>
                  <ul className="text-sm text-sand-800 space-y-1">
                    {details.first_blocker.missing.map((reason, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                        {reason}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={(e) => { e.stopPropagation(); unlockModule(details.first_blocker.module_id, details.first_blocker.module_title); }}
                    disabled={unlocking === details.first_blocker.module_id}
                    className="mt-3 flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 text-sm font-medium transition-colors"
                    data-testid={`quick-unlock-${details.first_blocker.module_id}`}
                  >
                    {unlocking === details.first_blocker.module_id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Unlock className="w-4 h-4" />
                    )}
                    Dieses Modul jetzt manuell freischalten
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-6 p-4 rounded-xl bg-green-500/5 border border-green-500/30">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <p className="text-green-400 font-medium">Alle freigeschalteten Module abgeschlossen — Teilnehmer ist im Plan.</p>
              </div>
            </div>
          )}
          
          {/* Module List */}
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <h4 className="font-semibold text-sand-500">Modulfortschritt:</h4>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCourseLearningTimeToSoll(true)}
                disabled={bulkSettingSoll}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sage/15 hover:bg-sage/25 text-sage-dark text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Nur Module mit 0 Min auf Soll setzen (sicherer Modus — Module mit teilweiser Lernzeit bleiben unangetastet)"
                data-testid={`bulk-soll-only-zero-${participant.user_id}`}
              >
                {bulkSettingSoll ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                Nur 0-Min auf Soll
              </button>
              <button
                onClick={() => setCourseLearningTimeToSoll(false)}
                disabled={bulkSettingSoll}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-terra/15 hover:bg-terra/25 text-terra text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Alle Module unter Soll auf Soll setzen (auch teilweise Lernzeit wird hochgesetzt)"
                data-testid={`bulk-soll-all-${participant.user_id}`}
              >
                {bulkSettingSoll ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                Alle auf Soll
              </button>
            </div>
          </div>
          <div className="space-y-2 mb-6">
            {details.modules.map((m) => (
              <div 
                key={m.module_id}
                className={`group flex items-center justify-between p-3 rounded-lg transition-colors ${
                  m.completed ? 'bg-green-500/5 hover:bg-green-500/10' 
                  : !m.is_unlocked ? 'bg-cream-50 hover:bg-cream-200 opacity-60'
                  : 'bg-cream-200 hover:bg-cream-200'
                }`}
                data-testid={`module-row-${m.module_id}`}
              >
                <div className="flex items-center gap-3">
                  {m.completed ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : !m.is_unlocked ? (
                    <XCircle className="w-5 h-5 text-sand-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-sand-500" />
                  )}
                  <div>
                    <span className="text-sand-800">{m.title}</span>
                    {!m.is_unlocked && (
                      <span className="ml-2 text-xs text-sand-500">(gesperrt)</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-sand-500">{m.ue_count} UE</span>
                  
                  {/* Tasks Status */}
                  {m.tasks_total > 0 && (
                    <span className={`flex items-center gap-1 ${m.tasks_submitted >= m.tasks_total ? 'text-green-400' : 'text-sand-500'}`}>
                      <FileText className="w-3.5 h-3.5" />
                      {m.tasks_submitted}/{m.tasks_total}
                    </span>
                  )}
                  
                  {/* Learning Time with Edit Option */}
                  {editingModule === m.module_id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        value={newMinutes}
                        onChange={(e) => setNewMinutes(e.target.value)}
                        placeholder="Min"
                        className="w-16 px-2 py-1 bg-cream-200 border border-cream-300 rounded text-sand-800 text-sm"
                        autoFocus
                      />
                      <input
                        type="text"
                        value={correctionReason}
                        onChange={(e) => setCorrectionReason(e.target.value)}
                        placeholder="Grund (optional)"
                        className="w-32 px-2 py-1 bg-cream-200 border border-cream-300 rounded text-sand-800 text-sm"
                      />
                      <button
                        onClick={() => correctLearningTime(m.module_id, m.title)}
                        disabled={correcting}
                        className="p-1 text-green-400 hover:text-green-300"
                        title="Speichern"
                      >
                        {correcting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => {
                          setEditingModule(null);
                          setNewMinutes('');
                          setCorrectionReason('');
                        }}
                        className="p-1 text-red-400 hover:text-red-300"
                        title="Abbrechen"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span className={`${m.time_spent_minutes === 0 ? 'text-amber-400' : 'text-sand-500'}`}>
                        {m.time_spent_minutes} Min
                      </span>
                      {/* 1-Klick: Lernzeit auf Soll */}
                      {(() => {
                        const sollMin = (m.ue_count || 0) * 45;
                        const atOrAbove = m.time_spent_minutes >= sollMin;
                        if (sollMin <= 0) return null;
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLearningTimeToSoll(m.module_id, m.title, m.ue_count);
                            }}
                            disabled={settingSoll === m.module_id || atOrAbove}
                            className={`p-1 opacity-0 group-hover:opacity-100 transition-opacity ${atOrAbove ? 'text-sage-dark cursor-default' : 'text-sand-500 hover:text-terra'}`}
                            title={atOrAbove
                              ? `Bereits ≥ Soll (${sollMin} Min)`
                              : `1-Klick: Auf Soll ${sollMin} Min (${m.ue_count} UE) setzen`}
                            data-testid={`set-soll-btn-${m.module_id}`}
                          >
                            {settingSoll === m.module_id
                              ? <RefreshCw className="w-3 h-3 animate-spin" />
                              : <Zap className="w-3 h-3" />}
                          </button>
                        );
                      })()}
                      <button
                        onClick={() => {
                          setEditingModule(m.module_id);
                          setNewMinutes(m.time_spent_minutes.toString());
                        }}
                        className="p-1 text-sand-500 hover:text-terra opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Lernzeit manuell korrigieren"
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  
                  {m.quiz_passed ? (
                    <span className="text-green-400">Quiz: {Math.round(m.quiz_score)}%</span>
                  ) : m.quiz_attempted ? (
                    <span className="text-amber-400">Quiz: {Math.round(m.quiz_score)}% (n. best.)</span>
                  ) : (
                    <span className="text-sand-500">Quiz: -</span>
                  )}
                  
                  {/* Manual Unlock Button - for incomplete modules */}
                  {!m.completed && (
                    <button
                      onClick={(e) => { e.stopPropagation(); unlockModule(m.module_id, m.title); }}
                      disabled={unlocking === m.module_id}
                      className="p-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 hover:text-amber-300 opacity-0 group-hover:opacity-100 transition-all"
                      title="Modul manuell freischalten"
                      data-testid={`unlock-btn-${m.module_id}`}
                    >
                      {unlocking === m.module_id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Quick Actions Info */}
          <div className="mb-4 p-3 bg-cream-200 rounded-lg border border-cream-300">
            <p className="text-xs text-sand-500 flex items-center gap-2 flex-wrap">
              <Zap className="w-3 h-3 text-terra" />
              1-Klick auf Soll (UE × 45 min) |
              <Edit3 className="w-3 h-3" />
              Lernzeit manuell korrigieren |
              <Unlock className="w-3 h-3 text-amber-400" />
              Modul komplett freischalten
            </p>
          </div>
          
          {/* Certificate Section - show when all quizzes passed */}
          {(participant.ready_for_exam || participant.quizzes_passed >= participant.total_modules) && !participant.certificate_issued && (
            <div className="border-t border-cream-300 pt-4">
              {!showSignature ? (
                <button
                  onClick={() => setShowSignature(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-terra to-terra-dark text-sand-800 rounded-xl font-semibold hover:opacity-90 transition-opacity"
                >
                  <FileText className="w-5 h-5" />
                  Teilnahmebescheinigung erstellen
                </button>
              ) : (
                <div className="space-y-6">
                  <h4 className="font-semibold text-terra flex items-center gap-2">
                    <Pen className="w-5 h-5" />
                    Teilnahmebescheinigung erstellen
                  </h4>
                  <p className="text-sm text-sand-500">
                    Der Teilnehmer bestätigt mit seiner Unterschrift die Teilnahme an {participant.total_ue} Unterrichtseinheiten.
                  </p>
                  
                  {/* Participant Signature */}
                  <div className="bg-cream-50 p-4 rounded-xl border border-cream-300">
                    <SignatureCanvas 
                      onSave={setSignatureData}
                      onClear={() => setSignatureData('')}
                      label="Unterschrift des Teilnehmers (mit Maus oder Touch zeichnen):"
                    />
                    {signatureData && (
                      <div className="mt-2 text-xs text-green-400 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Teilnehmer-Unterschrift erfasst
                      </div>
                    )}
                  </div>
                  
                  {/* Instructor Signature */}
                  <div className="bg-cream-50 p-4 rounded-xl border border-cream-300">
                    <SignatureCanvas 
                      onSave={setInstructorSignatureData}
                      onClear={() => setInstructorSignatureData('')}
                      label="Unterschrift des Dozenten / Kursleiters:"
                    />
                    {instructorSignatureData && (
                      <div className="mt-2 text-xs text-green-400 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Dozenten-Unterschrift erfasst
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={generateCertificate}
                      disabled={!signatureData || !instructorSignatureData || generating}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-terra to-terra-dark text-sand-800 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      {generating ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <Download className="w-5 h-5" />
                      )}
                      PDF erstellen & herunterladen
                    </button>
                    <button
                      onClick={() => setShowSignature(false)}
                      className="px-6 py-3 bg-cream-300 text-sand-500 rounded-xl hover:bg-cream-300 transition-colors"
                    >
                      Abbrechen
                    </button>
                  </div>
                  
                  {(!signatureData || !instructorSignatureData) && (
                    <p className="text-xs text-amber-400">
                      Beide Unterschriften erforderlich: Teilnehmer und Dozent
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {participant.certificate_issued && (
            <div className="border-t border-cream-300 pt-4">
              <div className="flex items-center gap-2 text-green-400">
                <Award className="w-5 h-5" />
                <span>Teilnahmebescheinigung wurde bereits ausgestellt</span>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Aufgaben-Snapshot Modal */}
      {showTaskSnapshot && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowTaskSnapshot(false)}>
          <div className="bg-cream-100 rounded-2xl border border-cream-300 w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Sticky Header */}
            <div className="px-6 py-4 border-b border-cream-300 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-sand-800">Aufgaben-Snapshot</h2>
                <p className="text-sm text-sand-500">{participant.user_name}{taskSnapshot ? ` · ${taskSnapshot.course.title}` : ''}</p>
              </div>
              <div className="flex items-center gap-4">
                {taskSnapshot && (
                  <>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-terra">{taskSnapshot.summary.total_submitted}/{taskSnapshot.summary.total_required}</div>
                      <div className="text-xs text-sand-500">eingereicht ({taskSnapshot.summary.completion_percentage}%)</div>
                    </div>
                    <button 
                      onClick={downloadSnapshotPDF}
                      className="flex items-center gap-1.5 px-3 py-2 bg-terra/15 hover:bg-terra/25 text-terra rounded-lg text-sm font-medium border border-terra/30 transition-colors"
                      data-testid="download-snapshot-pdf"
                      title="Als PDF für Audit-Mappe herunterladen"
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </button>
                  </>
                )}
                <button onClick={() => setShowTaskSnapshot(false)} className="p-2 hover:bg-cream-300 rounded-lg" data-testid="close-task-snapshot">
                  <X className="w-5 h-5 text-sand-500" />
                </button>
              </div>
            </div>
            
            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingSnapshot && (
                <div className="flex items-center justify-center py-20">
                  <RefreshCw className="w-6 h-6 text-terra animate-spin" />
                </div>
              )}
              {taskSnapshot && taskSnapshot.modules.map(m => (
                <div key={m.module_id} className="mb-6">
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-cream-300">
                    <h3 className="text-base font-semibold text-sand-800">
                      Modul {m.module_order}: {cleanModuleTitle(m.module_title)}
                    </h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${m.tasks_submitted === m.tasks_total && m.tasks_total > 0 ? 'bg-green-500/20 text-green-400' : 'bg-cream-200 text-sand-500'}`}>
                      {m.tasks_submitted}/{m.tasks_total} Aufgaben
                    </span>
                  </div>
                  
                  {m.tasks.length === 0 ? (
                    <p className="text-xs text-sand-400 italic py-2">Keine Aufgaben in diesem Modul.</p>
                  ) : m.tasks.map(t => (
                    <div key={t.task_id} className={`mb-3 p-4 rounded-xl border ${t.submitted ? 'bg-sage/10 border-green-500/20' : 'bg-cream-50 border-cream-200'}`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${t.type === 'reflexion' ? 'bg-purple-500/20 text-purple-300' : t.type === 'fallbeispiel' ? 'bg-blue-500/20 text-blue-300' : 'bg-amber-500/20 text-amber-300'}`}>{t.type || 'aufgabe'}</span>
                            <span className="text-xs text-sand-500">min. {t.min_words} Wörter</span>
                            {t.submitted && (
                              <span className="text-xs text-green-400 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                {t.word_count} W.
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-medium text-sand-800">{t.title}</p>
                          <p className="text-xs text-sand-500 mt-1">{t.description}</p>
                        </div>
                      </div>
                      
                      {t.submitted ? (
                        <div className="mt-3 p-3 bg-cream-100 rounded-lg border border-cream-300">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] text-sand-500 uppercase tracking-wide">Antwort des Teilnehmers</span>
                            {t.submitted_at && (
                              <span className="text-[10px] text-sand-500">eingereicht: {new Date(t.submitted_at).toLocaleDateString('de-DE')}</span>
                            )}
                          </div>
                          <p className="text-sm text-sand-700 whitespace-pre-wrap leading-relaxed">{t.answer_text}</p>
                        </div>
                      ) : (
                        <div className="mt-2 flex items-center gap-2 text-xs text-sand-500 italic">
                          <XCircle className="w-3.5 h-3.5" />
                          Noch keine Antwort eingereicht
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Admin Participants Dashboard
const AdminParticipantsDashboard = ({ courseId, authHeader, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadParticipants = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/participants/${courseId}`,
        authHeader
      );
      setData(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Teilnehmer");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadParticipants();
  }, [courseId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-terra animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const readyCount = data.participants.filter(p => p.ready_for_exam).length;
  const certifiedCount = data.participants.filter(p => p.certificate_issued).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-terra">Teilnehmer-Übersicht</h2>
          <p className="text-sand-500">{data.course?.title}</p>
        </div>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-cream-200 text-sand-500 rounded-lg hover:bg-cream-300 transition-colors"
        >
          Zurück
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-cream-200 rounded-xl p-4 border border-cream-300">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-terra" />
            <div>
              <div className="text-2xl font-bold text-sand-800">{data.participants.length}</div>
              <div className="text-sm text-sand-500">Teilnehmer</div>
            </div>
          </div>
        </div>
        
        <div className="bg-cream-200 rounded-xl p-4 border border-cream-300">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-terra" />
            <div>
              <div className="text-2xl font-bold text-sand-800">{data.total_ue}</div>
              <div className="text-sm text-sand-500">UE gesamt</div>
            </div>
          </div>
        </div>
        
        <div className="bg-cream-200 rounded-xl p-4 border border-cream-300">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-400" />
            <div>
              <div className="text-2xl font-bold text-sand-800">{readyCount}</div>
              <div className="text-sm text-sand-500">Prüfungsbereit</div>
            </div>
          </div>
        </div>
        
        <div className="bg-cream-200 rounded-xl p-4 border border-cream-300">
          <div className="flex items-center gap-3">
            <Award className="w-8 h-8 text-terra" />
            <div>
              <div className="text-2xl font-bold text-sand-800">{certifiedCount}</div>
              <div className="text-sm text-sand-500">Bescheinigt</div>
            </div>
          </div>
        </div>
      </div>

      {/* Participants List */}
      <div className="space-y-3">
        {data.participants.length === 0 ? (
          <div className="text-center py-12 text-sand-500">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Noch keine Teilnehmer in diesem Kurs</p>
          </div>
        ) : (
          data.participants.map((p) => (
            <ParticipantRow 
              key={p.user_id}
              participant={p}
              authHeader={authHeader}
              onRefresh={loadParticipants}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default AdminParticipantsDashboard;
